import {
    CoreFetch,
    DocumentApi,
    FacilityApi,
    ListProviderDocumentsOrderEnum,
    ListProviderFacilitiesOrderEnum,
    ListProviderPayersOrderEnum,
    ListProviderProvidersOrderEnum,
    ListSupportDocumentsOrderEnum,
    ListSupportFacilitiesOrderEnum,
    ListSupportPayersOrderEnum,
    ListSupportProvidersOrderEnum,
    PayerApi,
    ProviderAdjustmentReportItem,
    ProviderApi,
    ProviderDocument,
    ProviderDocumentSortType,
    ProviderFacility,
    ProviderFacilitySortType,
    ProviderPayer,
    ProviderPayerSortType,
    ProviderProvider,
    ProviderProviderSortType,
    ProviderRemarkReportItem,
    ProviderTransmissionStatusReportItem,
    ProviderType,
    ReportApi,
    SupportDocumentSortType,
    SupportFacility,
    SupportFacilitySortType,
    SupportPayerSortType,
    SupportProvider,
    SupportProviderSortType,
} from '../../../api';
import { WorkerConfigurationApiMode } from '../worker-configuration.api';
import { getAdjustmentCode } from '../../dictionary/adjustment-codes';
import {
    formatPayerAdjustmentGroupLabel,
    normalizePayerAdjustmentGroup,
} from '../../dictionary/payer-adjustment-groups';
import { getRemarkCode } from '../../dictionary/remark-codes';
import { formatDateForBackend } from '../../primitives/date-utils';
import { createGroupRequestFilters, createRequestFilters } from '../../../ui/data-table/data-table.service';
import { FilterMethod, ICustomFilter } from '../../../ui/data-table/components/filters/filters.models';
import { subMonths } from 'date-fns';
import {
    CatalogDocument,
    CatalogFacility,
    CatalogPayer,
    CatalogProvider,
    CatalogCode,
    CatalogGroup,
    EnterCatalog,
} from './worker-automation.types';

export type CatalogProviderRole = 'billing' | 'rendering';

export const CATALOG_REPORT_PERIOD_LABEL = 'from the last month';

const PICKER_SEARCH_LIMIT = 100;

export function getCatalogPayerName(
    catalog: EnterCatalog | null | undefined,
    payerId: string,
    payerById?: Record<string, CatalogPayer>,
): string | undefined {
    return payerById?.[payerId]?.name ?? catalog?.payers.find(payer => payer.payerId === payerId)?.name;
}

export function formatConditionValueLabel(
    kind: 'payerId' | string,
    value: string,
    catalog: EnterCatalog | null | undefined,
    payerById?: Record<string, CatalogPayer>,
): string {
    if (kind === 'payerId') {
        return getCatalogPayerName(catalog, value, payerById) ?? value;
    }

    return value;
}

function humanizeEnumToken(value: string): string {
    return value
        .split('_')
        .filter(Boolean)
        .map(word => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
}

type CatalogPayerSource = Pick<ProviderPayer, 'id' | 'name' | 'nickname' | 'coverage'>;

type CatalogDocumentSource = Pick<ProviderDocument, 'id' | 'filename' | 'description'>;

function formatPayerDisplayName(payer: CatalogPayerSource): string {
    return payer.nickname?.trim() ? `${payer.name} (${payer.nickname})` : payer.name || '';
}

function formatPayerMeta(payer: CatalogPayerSource): string {
    const parts: string[] = [];

    if (payer.coverage) {
        parts.push(humanizeEnumToken(String(payer.coverage)));
    }

    if (payer.id) {
        parts.push(`Payer ID ${payer.id}`);
    }

    return parts.join(' · ');
}

function mapPayersToCatalog(payers: CatalogPayerSource[]): CatalogPayer[] {
    return payers
        .filter(payer => payer.id)
        .map(payer => ({
            payerId: payer.id!,
            name: formatPayerDisplayName(payer),
            meta: formatPayerMeta(payer),
        }))
        .sort((left, right) => left.name.localeCompare(right.name));
}

function mapDocumentsToCatalog(documents: CatalogDocumentSource[]): CatalogDocument[] {
    return documents
        .filter(document => document.id)
        .map(document => ({
            documentId: document.id!,
            name: document.filename || document.description || document.id!,
        }))
        .sort((left, right) => left.name.localeCompare(right.name));
}

async function searchSupportCatalogPayers(query: string, limit: number): Promise<CatalogPayer[]> {
    const term = query.trim();
    const filters = term
        ? createRequestFilters([
              {
                  filterId: 'filterName',
                  filterMethod: FilterMethod.LK,
                  filterType: 'String',
                  value: term,
              },
          ])
        : {};

    const payers = await CoreFetch<PayerApi>(PayerApi).listSupportPayers({
        limit,
        sort: [SupportPayerSortType.NAME],
        order: [ListSupportPayersOrderEnum.ASCENDING],
        filterActive: 'EQ:1',
        ...filters,
    });

    return mapPayersToCatalog(payers);
}

async function searchProviderCatalogPayers(query: string, limit: number): Promise<CatalogPayer[]> {
    const term = query.trim();
    const filters = createRequestFilters(
        term
            ? [
                  {
                      filterId: 'filterName',
                      filterMethod: FilterMethod.LK,
                      filterType: 'String',
                      value: term,
                  },
              ]
            : [],
    );

    const payers = await CoreFetch<PayerApi>(PayerApi).listProviderPayers({
        limit,
        sort: [ProviderPayerSortType.NAME],
        order: [ListProviderPayersOrderEnum.ASCENDING],
        ...filters,
    });

    return mapPayersToCatalog(payers);
}

export async function searchCatalogPayers(
    query: string,
    apiMode: WorkerConfigurationApiMode = 'support',
    limit: number = PICKER_SEARCH_LIMIT,
): Promise<CatalogPayer[]> {
    return apiMode === 'provider'
        ? searchProviderCatalogPayers(query, limit)
        : searchSupportCatalogPayers(query, limit);
}

async function searchSupportCatalogDocuments(query: string, limit: number): Promise<CatalogDocument[]> {
    const term = query.trim();
    const filters = term
        ? createRequestFilters([
              {
                  filterId: 'filterFilename',
                  filterMethod: FilterMethod.LK,
                  filterType: 'String',
                  value: term,
              },
          ])
        : {};

    const documents = await CoreFetch<DocumentApi>(DocumentApi).listSupportDocuments({
        limit,
        sort: [SupportDocumentSortType.CREATED],
        order: [ListSupportDocumentsOrderEnum.DESCENDING],
        ...filters,
    });

    return mapDocumentsToCatalog(documents);
}

async function searchProviderCatalogDocuments(query: string, limit: number): Promise<CatalogDocument[]> {
    const term = query.trim();
    const filters = term
        ? createRequestFilters([
              {
                  filterId: 'filterFilename',
                  filterMethod: FilterMethod.LK,
                  filterType: 'String',
                  value: term,
              },
          ])
        : {};

    const documents = await CoreFetch<DocumentApi>(DocumentApi).listProviderDocuments({
        limit,
        sort: [ProviderDocumentSortType.CREATED],
        order: [ListProviderDocumentsOrderEnum.DESCENDING],
        ...filters,
    });

    return mapDocumentsToCatalog(documents);
}

export async function searchCatalogDocuments(
    query: string,
    apiMode: WorkerConfigurationApiMode = 'support',
    limit: number = PICKER_SEARCH_LIMIT,
): Promise<CatalogDocument[]> {
    return apiMode === 'provider'
        ? searchProviderCatalogDocuments(query, limit)
        : searchSupportCatalogDocuments(query, limit);
}

async function fetchSupportCatalogPayersByIds(ids: string[]): Promise<CatalogPayer[]> {
    const filters: ICustomFilter[][] = [
        ids.map(id => ({
            filterId: 'filterId',
            filterMethod: FilterMethod.EQ,
            filterType: 'String',
            value: id,
        })),
    ];

    const payers = await CoreFetch<PayerApi>(PayerApi).listSupportPayers({
        ...createGroupRequestFilters(filters),
    });

    return mapPayersToCatalog(payers);
}

async function fetchProviderCatalogPayersByIds(ids: string[]): Promise<CatalogPayer[]> {
    const filters: ICustomFilter[][] = [
        ids.map(id => ({
            filterId: 'filterId',
            filterMethod: FilterMethod.EQ,
            filterType: 'String',
            value: id,
        })),
    ];

    const payers = await CoreFetch<PayerApi>(PayerApi).listProviderPayers({
        ...createGroupRequestFilters(filters),
    });

    return mapPayersToCatalog(payers);
}

export async function fetchCatalogPayersByIds(
    ids: string[],
    apiMode: WorkerConfigurationApiMode = 'support',
): Promise<CatalogPayer[]> {
    if (!ids.length) {
        return [];
    }

    return apiMode === 'provider' ? fetchProviderCatalogPayersByIds(ids) : fetchSupportCatalogPayersByIds(ids);
}

type CatalogProviderSource = Pick<
    ProviderProvider | SupportProvider,
    'id' | 'organization' | 'first' | 'middle' | 'last' | 'npi' | 'nickname' | 'type'
>;

type CatalogFacilitySource = Pick<ProviderFacility | SupportFacility, 'id' | 'name' | 'npi' | 'ein'>;

function formatProviderDisplayName(provider: CatalogProviderSource): string {
    const org = provider.organization?.trim();
    if (org) {
        return org;
    }

    const name = [provider.first, provider.middle, provider.last].filter(Boolean).join(' ').trim();
    return name || provider.nickname?.trim() || provider.id || '';
}

function formatProviderMeta(provider: CatalogProviderSource): string {
    const parts: string[] = [];

    if (provider.npi) {
        parts.push(`NPI ${provider.npi}`);
    }

    if (provider.type) {
        parts.push(humanizeEnumToken(String(provider.type)));
    }

    if (provider.id) {
        parts.push(`ID ${provider.id}`);
    }

    return parts.join(' · ');
}

function mapProvidersToCatalog(providers: CatalogProviderSource[]): CatalogProvider[] {
    return providers
        .filter(provider => provider.id)
        .map(provider => ({
            providerId: provider.id!,
            name: formatProviderDisplayName(provider),
            meta: formatProviderMeta(provider),
        }))
        .sort((left, right) => left.name.localeCompare(right.name));
}

function formatFacilityMeta(facility: CatalogFacilitySource): string {
    const parts: string[] = [];

    if (facility.npi) {
        parts.push(`NPI ${facility.npi}`);
    }

    if (facility.ein) {
        parts.push(`EIN ${facility.ein}`);
    }

    if (facility.id) {
        parts.push(`ID ${facility.id}`);
    }

    return parts.join(' · ');
}

function mapFacilitiesToCatalog(facilities: CatalogFacilitySource[]): CatalogFacility[] {
    return facilities
        .filter(facility => facility.id)
        .map(facility => ({
            facilityId: facility.id!,
            name: facility.name?.trim() || facility.id!,
            meta: formatFacilityMeta(facility),
        }))
        .sort((left, right) => left.name.localeCompare(right.name));
}

function buildProviderTypeFilter(role: CatalogProviderRole): string[] {
    if (role === 'billing') {
        return [`EQ:${ProviderType.BILLING}`];
    }

    return [`EQ:${ProviderType.RENDERING}`];
}

async function searchSupportCatalogProviders(
    query: string,
    role: CatalogProviderRole,
    limit: number,
): Promise<CatalogProvider[]> {
    const term = query.trim();
    const filterProviderType = buildProviderTypeFilter(role);

    const providers = await CoreFetch<ProviderApi>(ProviderApi).listSupportProviders({
        limit,
        filterActive: 'EQ:1',
        sort: [SupportProviderSortType.ORGANIZATION],
        order: [ListSupportProvidersOrderEnum.ASCENDING],
        filterProviderType,
        ...(term
            ? role === 'billing'
                ? { filterOrganization: [`LK:${term}%`] }
                : { filterFirstLast: [`LK:${term}%`] }
            : {}),
    });

    return mapProvidersToCatalog(providers);
}

async function searchProviderCatalogProviders(
    query: string,
    role: CatalogProviderRole,
    limit: number,
): Promise<CatalogProvider[]> {
    const term = query.trim();
    const filterProviderType = buildProviderTypeFilter(role);

    const providers = await CoreFetch<ProviderApi>(ProviderApi).listProviderProviders({
        limit,
        sort: [ProviderProviderSortType.ORGANIZATION],
        order: [ListProviderProvidersOrderEnum.ASCENDING],
        filterProviderType,
        ...(term
            ? role === 'billing'
                ? { filterOrganization: [`LK:${term}%`] }
                : { filterLast: [`LK:${term}%`] }
            : {}),
    });

    return mapProvidersToCatalog(providers);
}

export async function searchCatalogProviders(
    query: string,
    role: CatalogProviderRole,
    apiMode: WorkerConfigurationApiMode = 'support',
    limit: number = PICKER_SEARCH_LIMIT,
): Promise<CatalogProvider[]> {
    return apiMode === 'provider'
        ? searchProviderCatalogProviders(query, role, limit)
        : searchSupportCatalogProviders(query, role, limit);
}

async function fetchSupportCatalogProvidersByIds(ids: string[]): Promise<CatalogProvider[]> {
    const filters: ICustomFilter[][] = [
        ids.map(id => ({
            filterId: 'filterId',
            filterMethod: FilterMethod.EQ,
            filterType: 'String',
            value: id,
        })),
    ];

    const providers = await CoreFetch<ProviderApi>(ProviderApi).listSupportProviders({
        ...createGroupRequestFilters(filters),
    });

    return mapProvidersToCatalog(providers);
}

async function fetchProviderCatalogProvidersByIds(ids: string[]): Promise<CatalogProvider[]> {
    const filters: ICustomFilter[][] = [
        ids.map(id => ({
            filterId: 'filterId',
            filterMethod: FilterMethod.EQ,
            filterType: 'String',
            value: id,
        })),
    ];

    const providers = await CoreFetch<ProviderApi>(ProviderApi).listProviderProviders({
        ...createGroupRequestFilters(filters),
    });

    return mapProvidersToCatalog(providers);
}

export async function fetchCatalogProvidersByIds(
    ids: string[],
    apiMode: WorkerConfigurationApiMode = 'support',
): Promise<CatalogProvider[]> {
    if (!ids.length) {
        return [];
    }

    return apiMode === 'provider'
        ? fetchProviderCatalogProvidersByIds(ids)
        : fetchSupportCatalogProvidersByIds(ids);
}

async function searchSupportCatalogFacilities(query: string, limit: number): Promise<CatalogFacility[]> {
    const term = query.trim();
    const filters = term
        ? createRequestFilters([
              {
                  filterId: 'filterName',
                  filterMethod: FilterMethod.LK,
                  filterType: 'String',
                  value: term,
              },
          ])
        : {};

    const facilities = await CoreFetch<FacilityApi>(FacilityApi).listSupportFacilities({
        limit,
        filterActive: 'EQ:1',
        sort: [SupportFacilitySortType.NAME],
        order: [ListSupportFacilitiesOrderEnum.ASCENDING],
        ...filters,
    });

    return mapFacilitiesToCatalog(facilities);
}

async function searchProviderCatalogFacilities(query: string, limit: number): Promise<CatalogFacility[]> {
    const term = query.trim();
    const filters = term
        ? createRequestFilters([
              {
                  filterId: 'filterName',
                  filterMethod: FilterMethod.LK,
                  filterType: 'String',
                  value: term,
              },
          ])
        : {};

    const facilities = await CoreFetch<FacilityApi>(FacilityApi).listProviderFacilities({
        limit,
        sort: [ProviderFacilitySortType.NAME],
        order: [ListProviderFacilitiesOrderEnum.ASCENDING],
        ...filters,
    });

    return mapFacilitiesToCatalog(facilities);
}

export async function searchCatalogFacilities(
    query: string,
    apiMode: WorkerConfigurationApiMode = 'support',
    limit: number = PICKER_SEARCH_LIMIT,
): Promise<CatalogFacility[]> {
    return apiMode === 'provider'
        ? searchProviderCatalogFacilities(query, limit)
        : searchSupportCatalogFacilities(query, limit);
}

async function fetchSupportCatalogFacilitiesByIds(ids: string[]): Promise<CatalogFacility[]> {
    const filters: ICustomFilter[][] = [
        ids.map(id => ({
            filterId: 'filterId',
            filterMethod: FilterMethod.EQ,
            filterType: 'String',
            value: id,
        })),
    ];

    const facilities = await CoreFetch<FacilityApi>(FacilityApi).listSupportFacilities({
        ...createGroupRequestFilters(filters),
    });

    return mapFacilitiesToCatalog(facilities);
}

async function fetchProviderCatalogFacilitiesByIds(ids: string[]): Promise<CatalogFacility[]> {
    const filters: ICustomFilter[][] = [
        ids.map(id => ({
            filterId: 'filterId',
            filterMethod: FilterMethod.EQ,
            filterType: 'String',
            value: id,
        })),
    ];

    const facilities = await CoreFetch<FacilityApi>(FacilityApi).listProviderFacilities({
        ...createGroupRequestFilters(filters),
    });

    return mapFacilitiesToCatalog(facilities);
}

export async function fetchCatalogFacilitiesByIds(
    ids: string[],
    apiMode: WorkerConfigurationApiMode = 'support',
): Promise<CatalogFacility[]> {
    if (!ids.length) {
        return [];
    }

    return apiMode === 'provider'
        ? fetchProviderCatalogFacilitiesByIds(ids)
        : fetchSupportCatalogFacilitiesByIds(ids);
}

function compareCatalogCodes(left: string, right: string): number {
    const leftNumeric = /^\d+$/.test(left);
    const rightNumeric = /^\d+$/.test(right);

    if (leftNumeric && rightNumeric) {
        return Number(left) - Number(right);
    }

    return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
}

function compareCatalogCodesByCount(left: CatalogCode, right: CatalogCode): number {
    return (right.count ?? 0) - (left.count ?? 0) || compareCatalogCodes(left.code, right.code);
}

function catalogReportDateRange(): { end: string; start: string } {
    const end = new Date();
    const start = subMonths(end, 1);

    return {
        start: formatDateForBackend(start) ?? '',
        end: formatDateForBackend(end) ?? '',
    };
}

/** Report endpoints sometimes wrap rows or use alternate field names; normalize before mapping. */
function unwrapReportRows(value: unknown): Record<string, unknown>[] {
    if (Array.isArray(value)) {
        return value.filter(
            (row): row is Record<string, unknown> => Boolean(row) && typeof row === 'object' && !Array.isArray(row),
        );
    }

    if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>;
        for (const key of ['items', 'data', 'results', 'rows', 'content']) {
            if (key in record) {
                return unwrapReportRows(record[key]);
            }
        }
    }

    return [];
}

function readReportString(row: Record<string, unknown>, ...keys: string[]): string | undefined {
    for (const key of keys) {
        const value = row[key];
        if (value == null) {
            continue;
        }

        const text = String(value).trim();
        if (text) {
            return text;
        }
    }

    return undefined;
}

function readReportCount(row: Record<string, unknown>): number {
    const value = row.count ?? row.total ?? row.quantity;
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

async function readReportPayload(load: () => Promise<{ raw: Response }>): Promise<unknown> {
    const response = await load();
    return response.raw.json();
}

function buildCarcCodesFromAdjustmentReport(
    items: Array<ProviderAdjustmentReportItem | Record<string, unknown>>,
): CatalogCode[] {
    const counts = new Map<string, number>();

    for (const item of items) {
        const row = item as Record<string, unknown>;
        const reasonCode = readReportString(row, 'reasonCode', 'reason_code', 'code');
        if (!reasonCode) {
            continue;
        }

        counts.set(reasonCode, (counts.get(reasonCode) ?? 0) + readReportCount(row));
    }

    return [...counts.entries()]
        .map(([code, count]) => ({
            code,
            count,
            desc: getAdjustmentCode(code),
        }))
        .sort(compareCatalogCodesByCount);
}

function buildCarcGroupsFromAdjustmentReport(
    items: Array<ProviderAdjustmentReportItem | Record<string, unknown>>,
): CatalogGroup[] {
    const groupCounts = new Map<string, number>();

    for (const item of items) {
        const row = item as Record<string, unknown>;
        const group = readReportString(row, 'group', 'adjustmentGroup', 'adjustment_group');
        if (!group) {
            continue;
        }

        groupCounts.set(group, (groupCounts.get(group) ?? 0) + readReportCount(row));
    }

    return [...groupCounts.entries()]
        .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
        .map(([group, count]) => ({ group: normalizePayerAdjustmentGroup(group), count }));
}

function buildRarcCodesFromRemarkReport(
    items: Array<ProviderRemarkReportItem | Record<string, unknown>>,
): CatalogCode[] {
    const counts = new Map<string, number>();

    for (const item of items) {
        const row = item as Record<string, unknown>;
        const remarkCode = readReportString(row, 'remarkCode', 'remark_code', 'code');
        if (!remarkCode) {
            continue;
        }

        counts.set(remarkCode, (counts.get(remarkCode) ?? 0) + readReportCount(row));
    }

    return [...counts.entries()]
        .map(([code, count]) => ({
            code,
            count,
            desc: getRemarkCode(code),
        }))
        .sort(compareCatalogCodesByCount);
}

function buildRejectionStatusesFromTransmissionReport(
    items: Array<ProviderTransmissionStatusReportItem | Record<string, unknown>>,
): CatalogCode[] {
    const statuses: CatalogCode[] = [];

    for (const item of items) {
        const row = item as Record<string, unknown>;
        const message = readReportString(row, 'message', 'status', 'transmissionStatus', 'transmission_status');
        if (!message) {
            continue;
        }

        statuses.push({
            code: message,
            count: readReportCount(row),
            desc: message,
        });
    }

    return statuses.sort(compareCatalogCodesByCount);
}

export async function fetchEnterCatalog(): Promise<EnterCatalog> {
    const range = catalogReportDateRange();
    const reportApi = CoreFetch<ReportApi>(ReportApi);

    const [adjustmentsPayload, remarksPayload, transmissionPayload] = await Promise.all([
        readReportPayload(() => reportApi.getAdjustmentReportRaw(range)),
        readReportPayload(() => reportApi.getRemarkReportRaw(range)),
        readReportPayload(() => reportApi.getTransmissionStatusReportRaw(range)),
    ]);

    const adjustments = unwrapReportRows(adjustmentsPayload);
    const remarks = unwrapReportRows(remarksPayload);
    const transmissionStatuses = unwrapReportRows(transmissionPayload);

    return {
        carcCodes: buildCarcCodesFromAdjustmentReport(adjustments),
        carcGroups: buildCarcGroupsFromAdjustmentReport(adjustments),
        rarcCodes: buildRarcCodesFromRemarkReport(remarks),
        rejectionStatuses: buildRejectionStatusesFromTransmissionReport(transmissionStatuses),
        payers: [],
        documents: [],
    };
}

export function formatCatalogSyncLabel(date: Date = new Date()): string {
    return `Loaded ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

export function formatCatalogCountLabel(catalog: EnterCatalog): string {
    return `${catalog.carcCodes.length} CARC · ${catalog.rarcCodes.length} RARC · ${catalog.carcGroups.length} CARC groups · ${catalog.rejectionStatuses.length} transmission statuses`;
}

export function formatCatalogGroupLabel(group: string): string {
    return formatPayerAdjustmentGroupLabel(group);
}
